/**
 * The Workflow chapter — slide content (data only, no JSX).
 *
 * The big picture: how everything in ALIS is connected, and every
 * path work can take through it. Infographic artwork lives in
 * /public/how-it-works/img-workflow (generated series, labels in-image;
 * `overlayLabels` is the crisp-HTML fallback for any diagram whose baked
 * text didn't come out clean).
 */
import type { TourSlideBase } from './tourCore';

export interface WorkflowSlideDef extends TourSlideBase {
  /** Big display headline. */
  title: string;
  /** One caption paragraph under the diagram. */
  caption: string;
  /** Optional fact chips under the caption. */
  chips?: string[];
  /** Fallback labels when a diagram ships text-free. */
  overlayLabels?: { xPct: number; yPct: number; text: string }[];
}

export const WORKFLOW_SLIDES: WorkflowSlideDef[] = [
  {
    id: 'map',
    kicker: 'The big picture',
    title: 'One connected flow, end to end.',
    caption:
      'Everything is one pipeline: work enters through a quote or goes straight to a work order, gets scheduled, tested, reviewed and reported — and what you tell us afterwards loops right back into making it better. No hand-offs between systems, because there are no other systems.',
    image: '/how-it-works/img-workflow/map.webp',
    alt: 'Map of the full workflow: quote, approval, work order, schedule, test, report, with a feedback loop',
    accent: 'sage',
    chips: ['Quote', 'Approval', 'Work Order', 'Schedule', 'Test', 'Report', 'Feedback'],
  },
  {
    id: 'quote-path',
    kicker: 'Path one · the quote',
    title: 'Two kinds of quotes. One work order.',
    caption:
      'External quotes go to clients: build with real catalog prices, route through internal approval, send it out — the client accepts with one click (or you mark it accepted) and the work order is created automatically, tests and all. Internal quotes, for BGC’s own projects, skip the approval round-trip and head straight to work. Nothing is re-typed either way.',
    image: '/how-it-works/img-workflow/quote-path.webp',
    alt: 'External quotes flowing through approval and acceptance, internal quotes bypassing approval, both converging on one automatically created work order',
    accent: 'amber',
    chips: ['External · approved & accepted', 'Internal · no approval needed'],
  },
  {
    id: 'direct-path',
    kicker: 'Path two · straight to work',
    title: 'Or skip the quote entirely.',
    caption:
      'Internal job? Standing agreement? Create the work order directly. Both doors lead to the exact same place — one work order, one record, one downstream flow. The lab never has to care which door the job came through.',
    image: '/how-it-works/img-workflow/direct-path.webp',
    alt: 'Two doors, a quote path and a direct path, merging into one work order',
    accent: 'moss',
  },
  {
    id: 'wo-anatomy',
    kicker: 'Inside the work order',
    title: 'Locations hold samples. Samples get tests.',
    caption:
      'A work order is a living hierarchy: the site locations it covers, the samples collected at each one, and the tests to run on every sample. Add a sample or attach a test, and scheduling, testing and reporting all see it instantly.',
    image: '/how-it-works/img-workflow/wo-anatomy.webp',
    alt: 'Hierarchy from work order to locations to samples to tests',
    accent: 'sage',
    chips: ['Locations', 'Samples', 'Tests'],
  },
  {
    id: 'plan',
    kicker: 'Scheduling',
    title: 'The scenario planner weighs everything.',
    caption:
      'Machines and their capabilities. Your people and their skill levels, 1 to 5. Who needs an experienced BGCer working beside them. Who is on vacation. It drafts a plan, flags every conflict, and then hands you the controls — drag, drop, split and rebalance until it is exactly right, then commit it to the live schedule.',
    image: '/how-it-works/img-workflow/plan.webp',
    alt: 'A planning board fed by machines, skills, people and availability',
    accent: 'slate',
    chips: ['Machines', 'People', 'Skills 1–5', 'Availability', 'Conflicts', 'Drag & drop'],
  },
  {
    id: 'test-report',
    kicker: 'Testing & reporting',
    title: 'Readings in. Report out.',
    caption:
      'Record the readings — or upload the file — and every calculation, curve and classification happens live, verified against the spreadsheets the lab already trusts. A skill-qualified reviewer signs off, and one click renders the polished PDF in the shared house style.',
    image: '/how-it-works/img-workflow/test-report.webp',
    alt: 'Pipeline from machine readings through calculations and review to a finished report',
    accent: 'amber',
  },
  {
    id: 'loop',
    kicker: 'The loop never stops',
    title: 'You talk. It changes. Every week.',
    caption:
      'The feedback button lives on every page. Tell us what is wrong, what is missing, or what would make your day easier — it lands in our queue, gets built, and ships in the next release. That is the whole workflow: work flows through, feedback flows back.',
    image: '/how-it-works/img-workflow/loop.webp',
    alt: 'Circular loop of feedback, building and releasing around a growing plant',
    accent: 'night',
    chips: ['Feedback', 'We build', 'Release'],
  },
];
